import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import AppState from '@webapp/store/state';
import { UpdateTrackField } from '@webapp/store/tracks/action-creators';
import {
  ChangeReleaseInputField,
  UpdateReleaseStoreError,
} from '@webapp/store/release/action-creators';

import InputField from '@components/form/input';
import TrackIcon from '@components/basics/icons/track/track-icon';
import ErrorMessage from '@webapp/components/basics/error-message/index';

import { CleanTitleHintCtrl, isTitleClean } from '../clean-title-hint/index';

interface SongTitleCtrlProps {
  className?: string;
  releaseId: string;
  trackId: string;
  label: React.ReactNode;
  disableRemove?: boolean;
  locked?: boolean;
  validate?: boolean;
}

interface StoreProps {
  isSingle: boolean;
  value: string;
  onChange: (
    value: string,
    isSingle: boolean
  ) => ChangeReleaseInputField<'title'> | UpdateTrackField<'title'>;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
}

const mapStateToProps = (state: AppState, props: SongTitleCtrlProps) => ({
  value: state.tracks.tracks[props.trackId].title || '',
  isSingle: state.release.release?.type === 'single',
});

const mapDispatchToProps = (dispatch: Dispatch, props: SongTitleCtrlProps) => ({
  onChange: (value: string, isSingle: boolean) => {
    return isSingle
      ? dispatch(new ChangeReleaseInputField<'title'>('title', value))
      : dispatch(new UpdateTrackField(props.trackId, 'title', value));
  },
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(error, field, value, releaseId)),
});

export const SongTitleCtrl: React.FC<SongTitleCtrlProps & StoreProps> = ({
  trackId,
  value,
  validate = false,
  releaseId,
  ...props
}) => {
  const [customValue, setCustomValue] = React.useState(value || '');
  const [currentTrack, setCurrentTrack] = React.useState(trackId);

  const { sendAnalytics, isSingle, onChange } = props;

  React.useEffect(() => {
    if (trackId !== currentTrack) {
      setCustomValue(value);
      setCurrentTrack(trackId);
    }
  }, [trackId, currentTrack, value]);

  const send = React.useCallback(() => {
    sendAnalytics(
      'Release Track Name',
      currentTrack,
      'RELEASE.MISSING_SONGNAME',
      releaseId
    );
  }, [currentTrack, sendAnalytics, releaseId]);

  React.useEffect(() => {
    if (validate && !customValue) {
      send();
    }
  }, [validate, releaseId, send, customValue]);

  React.useEffect(() => {
    onChange(customValue, isSingle);
  }, [customValue, isSingle, onChange]);

  const handleChange = (value: string) => {
    if (props.locked) {
      return;
    }
    setCustomValue(value);
    onChange(value, isSingle);
  };

  return (
    <div className={props.className}>
      <InputField
        label={props.label}
        value={customValue}
        type="text"
        locked={props.locked}
        onChange={handleChange}
        rightDecoration={<TrackIcon />}
        customBorderRadius={!isTitleClean(value) ? '4px 4px 0 0' : undefined}
      />

      <CleanTitleHintCtrl title={value || ''} />

      {validate && !customValue && (
        <ErrorMessage
          hasBackground
          className="mt-5"
          errorId="RELEASE.MISSING_SONGNAME"
        />
      )}
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(SongTitleCtrl);
